import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { DunningCandidate } from '../types';

const levelLabels = ['Keine', 'Zahlungserinnerung', '1. Mahnung', '2. Mahnung'];
const levelColors = [
  'bg-slate-50 text-slate-600 border-slate-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-orange-50 text-orange-700 border-orange-200',
  'bg-red-50 text-red-700 border-red-200',
];
const nextLevelLabels = ['Zahlungserinnerung senden', '1. Mahnung senden', '2. Mahnung senden'];

export default function DunningPage() {
  const qc = useQueryClient();

  const { data: candidates = [], isLoading } = useQuery<DunningCandidate[]>({
    queryKey: ['dunning-candidates'],
    queryFn: () => api.get('/invoices/dunning/candidates').then((r) => r.data),
  });

  const escalateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/invoices/${id}/dunning`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dunning-candidates'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  const fmt = (n: number) => Number(n).toFixed(2).replace('.', ',');

  const totalOverdue = candidates.reduce((s, c) => s + c.totalGross, 0);
  const totalFees = candidates.reduce((s, c) => s + c.dunningFee + c.newFee, 0);

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Mahnwesen</h1>
          <p className="sp-page-subtitle">
            {candidates.length} überfällige Rechnungen &middot; {fmt(totalOverdue)} EUR offen
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="sp-stat-card">
          <span className="sp-stat-label">Überfällig</span>
          <span className="sp-stat-value text-red-600">{candidates.length}</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Offener Betrag</span>
          <span className="sp-stat-value">{fmt(totalOverdue)} EUR</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Mahngebühren</span>
          <span className="sp-stat-value">{fmt(totalFees)} EUR</span>
        </div>
        <div className="sp-stat-card">
          <span className="sp-stat-label">Älteste Rechnung</span>
          <span className="sp-stat-value">
            {candidates.length > 0 ? `${Math.max(...candidates.map((c) => c.daysPastDue))} Tage` : '-'}
          </span>
        </div>
      </div>

      {/* Dunning Table */}
      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Rechnungsnr.</th>
              <th>Kunde / Projekt</th>
              <th>Fällig seit</th>
              <th>Mahnstufe</th>
              <th className="text-right">Betrag</th>
              <th className="text-right">Gebühren</th>
              <th className="w-48 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7}><div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <svg className="sp-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                Laden...</div></td></tr>
            ) : candidates.length === 0 ? (
              <tr><td colSpan={7}><div className="sp-empty">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-emerald-700">Keine überfälligen Rechnungen</span>
                <span className="text-xs text-slate-400">Alle Rechnungen wurden rechtzeitig bezahlt</span>
              </div></td></tr>
            ) : candidates.map((c) => (
              <tr key={c.id}>
                <td className="font-mono text-xs font-semibold text-slate-800">{c.number}</td>
                <td>
                  <div>
                    <span className="text-slate-800 font-medium">{c.project.customer.name}</span>
                    <span className="block text-[11px] text-slate-400">{c.project.title}</span>
                  </div>
                </td>
                <td>
                  <div>
                    <span className="text-red-600 font-semibold text-xs">{c.daysPastDue} Tage</span>
                    <span className="block text-[11px] text-slate-400">
                      seit {new Date(c.dueDate).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                </td>
                <td>
                  <span className={`sp-badge border ${levelColors[c.dunningLevel]}`}>
                    {levelLabels[c.dunningLevel]}
                  </span>
                </td>
                <td className="text-right font-mono text-xs font-semibold text-slate-800">{fmt(c.totalGross)} EUR</td>
                <td className="text-right">
                  {(c.dunningFee > 0 || c.newFee > 0) && (
                    <span className="text-xs text-orange-600 font-medium">
                      {fmt(c.dunningFee + c.newFee)} EUR
                      {c.interestAmount > 0 && <span className="block text-[10px]">+ {fmt(c.interestAmount)} EUR Zinsen</span>}
                    </span>
                  )}
                </td>
                <td className="text-right">
                  {c.nextLevel <= 3 && (
                    <button
                      onClick={() => {
                        if (confirm(`${nextLevelLabels[c.nextLevel - 1]} für Rechnung ${c.number}?`)) {
                          escalateMutation.mutate(c.id);
                        }
                      }}
                      disabled={escalateMutation.isPending}
                      className="sp-btn-primary !py-1.5 !px-3 !text-xs"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      {nextLevelLabels[c.nextLevel - 1]}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">Mahnstufen</h4>
        <div className="grid grid-cols-3 gap-4 text-xs text-blue-700">
          <div>
            <span className="font-semibold">Stufe 1 — Zahlungserinnerung</span>
            <p className="text-blue-600 mt-0.5">Freundliche Erinnerung nach Fälligkeitsdatum. Keine Gebühren.</p>
          </div>
          <div>
            <span className="font-semibold">Stufe 2 — 1. Mahnung</span>
            <p className="text-blue-600 mt-0.5">14 Tage nach Erinnerung. Mahngebühr: 5,00 EUR.</p>
          </div>
          <div>
            <span className="font-semibold">Stufe 3 — 2. Mahnung</span>
            <p className="text-blue-600 mt-0.5">14 Tage nach 1. Mahnung. Mahngebühr: 10,00 EUR + Verzugszinsen (5% p.a.).</p>
          </div>
        </div>
      </div>
    </div>
  );
}
